import { useState } from "react";
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Check } from "lucide-react";
import { BRAND } from "../data/content";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    guests: "",
    date: "",
    time: "",
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    // UI only — no backend submission per scope.
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: "", email: "", phone: "", city: "", guests: "", date: "", time: "", message: "" });
  };

  return (
    <div data-testid="contact-page" className="pt-28">
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Reserve · Say Hello</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1]">
            Reserve your <em className="italic text-[#C76D4B]">corner.</em>
          </h1>
          <p className="mt-8 text-[#7A5C45] max-w-xl mx-auto">
            Tell us when you're coming and how big the table needs to be. We'll save you a corner —
            and a really good cup.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* DETAILS */}
          <aside className="lg:col-span-4 bg-[#2A1E17] text-[#FAF7F2] rounded-[2rem] p-8 sm:p-10 flex flex-col">
            <div className="text-xs tracking-[0.22em] uppercase text-[#D4A373]">In touch</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight">
              Always around for a <em className="italic text-[#D4A373]">conversation.</em>
            </h2>
            <ul className="mt-10 space-y-5 text-sm">
              <li className="flex gap-3"><MapPin size={18} className="text-[#D4A373] mt-1 flex-none" /> {BRAND.contact.address}</li>
              <li className="flex gap-3"><Phone size={18} className="text-[#D4A373] mt-1 flex-none" /> <a href={`tel:${BRAND.contact.phone}`} data-testid="contact-phone">{BRAND.contact.phone}</a></li>
              <li className="flex gap-3"><Mail size={18} className="text-[#D4A373] mt-1 flex-none" /> <a href={`mailto:${BRAND.contact.email}`} data-testid="contact-email">{BRAND.contact.email}</a></li>
              <li className="flex gap-3"><Clock size={18} className="text-[#D4A373] mt-1 flex-none" /> {BRAND.contact.hours}</li>
            </ul>
            <div className="mt-auto pt-12">
              <p className="font-script text-3xl text-[#D4A373]">— see you soon</p>
            </div>
          </aside>

          {/* FORM */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-8 bg-[#FAF7F2] rounded-[2rem] border border-[#E8D8C8] p-8 sm:p-12"
          >
            {submitted && (
              <div data-testid="contact-success" className="mb-8 flex items-center gap-3 bg-[#7B8B6F]/12 border border-[#7B8B6F]/30 text-[#4A3320] px-5 py-4 rounded-2xl">
                <Check size={18} className="text-[#7B8B6F]" />
                <span className="text-sm">Thank you — your request has been noted. Our team will reach out shortly.</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Your name</label>
                <input data-testid="form-name" required className="input-line" value={form.name} onChange={update("name")} placeholder="Aarav Khanna" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Email</label>
                <input data-testid="form-email" required type="email" className="input-line" value={form.email} onChange={update("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Phone</label>
                <input data-testid="form-phone" required className="input-line" value={form.phone} onChange={update("phone")} placeholder="+91 ..." />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">City / Café</label>
                <input data-testid="form-city" className="input-line" value={form.city} onChange={update("city")} placeholder="Bengaluru · Indiranagar" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Date</label>
                <input data-testid="form-date" type="date" className="input-line" value={form.date} onChange={update("date")} />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Time</label>
                <input data-testid="form-time" type="time" className="input-line" value={form.time} onChange={update("time")} />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Guests</label>
                <input data-testid="form-guests" className="input-line" value={form.guests} onChange={update("guests")} placeholder="2 / 4 / 6+" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Occasion (optional)</label>
                <input data-testid="form-occasion" className="input-line" placeholder="Date · Catch-up · Birthday" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Tell us anything else</label>
                <textarea
                  data-testid="form-message"
                  className="input-line min-h-[100px] resize-none"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Allergies, accessibility needs, birthday surprises…"
                />
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
              <p className="text-xs text-[#7A5C45]/80 max-w-md">
                Submitting this form will share your details with the nearest Stories café. We
                respect your privacy.
              </p>
              <button type="submit" className="btn-primary" data-testid="form-submit">
                Send Request <ArrowUpRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
